import {inject, Injectable} from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';

import {environment} from "../../../environments/environment";
import Hotjar from '@hotjar/browser';
import {NavigationEnd, Router} from "@angular/router";
import {UserService} from "./user.service";
import {distinctUntilChanged, filter, map, merge, take} from "rxjs";
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser";
import {BotDetectionService} from "./bot-detection.service";
import {PlatformService} from "./platform.service";
import {Capacitor} from "@capacitor/core";

const amplitudeReturn = amplitude.init(environment.amplitudeKey,
  {
    serverUrl: environment.amplitudeServerUrl,
    autocapture: {elementInteractions: true},
    logLevel: amplitude.Types.LogLevel.Warn,
  });

const platformMap: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

const rawPlatform = Capacitor.getPlatform();
const mappedPlatform = platformMap[rawPlatform] ?? 'Unknown';

const platformPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'PlatformPlugin',
  type: 'enrichment',
  /** No one‑time setup needed here */
  setup: async () => {},

  /** Called with each event; must return the (possibly mutated) event */
  execute: async (event) => {
    event.platform = mappedPlatform;
    return event;
  },
};

// Register it
amplitude.add(platformPlugin);


type AmplitudeEvent = 'signup' |
  'paid' |
  'no-preferences-after-signup' |
  'resubscribe-payment-started' |
  'resubscribe-flow-started' |
  'fix-past-due-started' |
  'payment-started' |
  'payment-success' |
  'address-flow-started' |
  'address-flow-success' |
  'household-flow-started' |
  'household-flow-success' |
  'income-flow-started' |
  'income-flow-success' |
  'mandatory-flow-success' |
  'signup-success' |
  'signup-failed' |
  'signup-start' |
  'login-after-signup' |
  'login-start' |
  'login-success' |
  'login-failed' |
  'password-reset-requested' |
  'ineligible-page' |
  'stay-homey-discount-opened' |
  'stay-homey-discount-applied' |
  'stay-homey-discount-ignored' |
  'payment-canceled' |
  'refer-button-pressed' |
  'referral-link-copied' |
  'referral-link-shared' |
  'visible-error' |
  'homey-share' |
  'share-apartment' |
  'push-notification-clicked' |
  'housing-connect-password-got' |
  'promotion-header-clicked' |
  'rate-homey-rating-submitted' |
  'rate-homey-description-submitted' |
  'click-tracked';

type PaymentEventProperties = {
  flow: 'initial' | 'resubscribe',
  checkoutLocation: 'default' | 'deferred' | 'deferred-no-trial',
  interval: 'monthly' | 'annual' | 'unknown' | 'revenuecat' | 'deferred',
};


@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly botDetectionService = inject(BotDetectionService);

  public async sendEvent(event: AmplitudeEvent, properties?: Record<string, string | number | undefined>) {
    try {
      await amplitudeReturn.promise;
      await amplitude.track(`[Homey] ${event}`, properties).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to send event', e);
    }
  }

  public async setClickSource(source: string, url: string) {
    if (this.platformService.isServer) {
      return;
    }

    try {
      this.sendEvent('click-tracked', { click_source: source, url: url });
      await amplitudeReturn.promise;
      const identify = new amplitude.Identify().set('click_source', source);
      await amplitude.identify(identify).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to set click campaign', e);
    }
  }

  public buildPaymentEventProperties(
    base: {
      flow: PaymentEventProperties['flow'];
      checkoutLocation: PaymentEventProperties['checkoutLocation'];
      interval: PaymentEventProperties['interval'];
    }
  ): PaymentEventProperties {
    return {
      ...base,
      interval: base.interval ?? 'unknown',
    };
  }

  public async sendPaymentStarted(params: {
    flow: 'initial' | 'resubscribe',
    interval: 'monthly' | 'annual',
    checkoutLocation: 'default' | 'deferred' | 'deferred-no-trial',
  }) {
    try {
      const event = params.flow === 'resubscribe' ? 'resubscribe-payment-started' : 'payment-started';
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        interval: params.interval,
        checkoutLocation: params.checkoutLocation,
      });

      await this.sendEvent(event, properties);
    } catch (err) {
      console.error('sendPaymentStarted analytics error', err);
    }
  }

  public async sendPaymentSuccess(params: {
    flow: 'initial' | 'resubscribe',
    checkoutLocation: 'default' | 'deferred' | 'deferred-no-trial',
    interval: PaymentEventProperties['interval'],
  }) {
    try {
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        checkoutLocation: params.checkoutLocation,
        interval: params.interval,
      });
      await this.sendEvent('payment-success', properties);
      this.trackFacebookPurchase(properties);
    } catch (err) {
      console.error('sendPaymentSuccess analytics error', err);
    }
  }

  public getUTMData(): {utmSource: string, utmCampaign: string} {
    const prefix = environment.amplitudeKey.slice(0, 10);
    const cookieName = `AMP_MKTG_${prefix}`;
    let utmSource = 'unknown';
    let utmCampaign = 'unknown';

    try {
      // grab the cookie value
      const raw = document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${cookieName}=`))
        ?.split('=')[1];

      if (raw) {
        // parse JSON safely
        const uriEncoded = atob(raw);                         // :contentReference[oaicite:1]{index=1}
        const data = JSON.parse(decodeURIComponent(uriEncoded));
        // pick out only what you need, falling back to defaults
        utmSource   = data.utm_source   ?? utmSource;
        utmCampaign = data.utm_campaign ?? utmCampaign;
      }
    } catch (err) {
      // swallow any errors—defaults will stay in place
      console.error('getUTMData error:', err);
    }

    return { utmSource, utmCampaign };
  }

  public initialize() {
    if (this.platformService.isServer) {
      return;
    }

    try {
      amplitudeReturn.promise.then(() => {
          this.userService.user$.pipe(
            map(user => user.status !== 'unauthorized' ? user.id : undefined),
            filter(id => !!id),
            distinctUntilChanged(),
          ).subscribe(id => {
            amplitude.setUserId(id);
          });

          amplitude.flush();

          this.setupSessionRecording();

          window.addEventListener('pagehide',
            () => {
              amplitude.setTransport('beacon')
              amplitude.flush()
            },
          );
        }
      )
    } catch (e) {
      console.error('Failed to initialize Amplitude', e);
    }

    try {
      if (environment.hotjarSiteId) {
        Hotjar.init(environment.hotjarSiteId, environment.hotjarVersion);

        this.router.events.subscribe(event => {
          if (event instanceof NavigationEnd) {
            Hotjar.stateChange(event.url);
          }
        });
      }
    } catch (e) {
      console.error('Failed to initialize Hotjar', e);
    }
  }

  async setupSessionRecording() {
    if (environment.amplitude.sessionRecordingEnabled) {
      try {
        const bot = await this.botDetectionService.isBot();

        if (bot) {
          console.log('Bot detected, session recording disabled');
          return;
        }

        const relevantPage = this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          filter(event => event.url.includes('signup') || event.url.includes('login')),
        );

        const loggedInUser = this.userService.user$.pipe(
          filter(user => user.status !== 'unauthorized'),
        );

        merge(relevantPage, loggedInUser).pipe(
          take(1),
        ).subscribe(() => {
          amplitude.add(
            sessionReplayPlugin({
              sampleRate: 1,
            }),
          );

          console.log('Session recording started');
        });

      } catch (e) {
        console.error('Failed to setup session recording', e);
      }
    }
  }

  private trackFacebookPurchase(properties: PaymentEventProperties) {
    if (this.platformService.isServer) {
      return;
    }

    const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof fbq !== 'function') {
      return;
    }

    try {
      fbq('track', 'Purchase', {
        flow: properties.flow,
        checkoutLocation: properties.checkoutLocation,
        interval: properties.interval,
      });
    } catch (err) {
      console.error('Facebook pixel track error', err);
    }
  }
}
